import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [allUsers, setAllUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchAllUsers();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await api.getProjects();
      setProjects(data);
    } catch (err) {
      alert('Failed to fetch projects: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const data = await api.getUsers();
      setAllUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createProject(formData);
      alert('Project created successfully');
      setShowModal(false);
      setFormData({ name: '', description: '' });
      fetchProjects();
    } catch (err) {
      alert(err.message || 'Failed to create project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await api.deleteProject(id);
      alert('Project deleted');
      fetchProjects();
    } catch (err) {
      alert(err.message || 'Failed to delete project');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      
      // Validate required fields
      if (!json.project) {
        throw new Error('Invalid backup: missing project data');
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/projects/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          project: JSON.stringify(json.project),
          receipts: JSON.stringify(json.receipts || []),
          contacts: JSON.stringify(json.contacts || []),
          members: JSON.stringify(json.members || [])
        })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Import failed');
      }
      
      alert('Project imported successfully!');
      setShowImportModal(false);
      fetchProjects();
    } catch (err) {
      alert('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleBackup = async (project) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/projects/${project.id}/backup`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download backup');
      }
      
      const data = await response.json();
      
      // Create downloadable file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name.replace(/[^a-z0-9]/gi, '_')}_backup.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download backup: ' + err.message);
    }
  };

  const openMembersModal = async (project) => {
    setSelectedProject(project);
    try {
      const members = await api.getProjectMembers(project.id);
      setProjectMembers(members);
      setShowMembersModal(true);
    } catch (err) {
      alert('Failed to fetch members');
    }
  };

  const handleAddMember = async (userId) => {
    try {
      await api.addProjectMember(selectedProject.id, parseInt(userId));
      alert('Member added');
      const members = await api.getProjectMembers(selectedProject.id);
      setProjectMembers(members);
      setShowAddMemberModal(false);
    } catch (err) {
      alert(err.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await api.removeProjectMember(selectedProject.id, userId);
      const members = await api.getProjectMembers(selectedProject.id);
      setProjectMembers(members);
    } catch (err) {
      alert(err.message || 'Failed to remove member');
    }
  };

  const getAvailableUsers = () => {
    const memberIds = projectMembers.map(m => m.id);
    return allUsers.filter(u => !memberIds.includes(u.id));
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="projects-page">
      <div className="page-header">
        <h1>📁 Projects Management</h1>
        <div className="header-actions">
          <button onClick={() => window.location.href = '/admin'} className="btn btn-secondary">
            ← Back to Config
          </button>
          <button onClick={() => setShowModal(true)} className="btn btn-primary">
            + New Project
          </button>
          <button onClick={() => setShowImportModal(true)} className="btn btn-secondary" style={{ marginLeft: '8px' }}>
            📥 Import
          </button>
        </div>
      </div>

      <div className="admin-section">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Description</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map(project => (
              <tr key={project.id}>
                <td>{project.id}</td>
                <td><strong>{project.name}</strong></td>
                <td>{project.description || '-'}</td>
                <td>{new Date(project.created_at).toLocaleDateString()}</td>
                <td>
                  <button onClick={() => openMembersModal(project)} className="btn btn-sm btn-outline">
                    👥 Members
                  </button>
                  <button onClick={() => handleBackup(project)} className="btn btn-sm btn-outline" style={{ marginLeft: '8px' }}>
                    💾 Backup
                  </button>
                  <button 
                    onClick={() => handleDelete(project.id)} 
                    className="btn btn-sm btn-danger"
                    style={{ marginLeft: '8px' }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {projects.length === 0 && (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', color: '#666' }}>
                  No projects found. Create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Project Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Create New Project</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Project Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Members Modal */}
      {showMembersModal && (
        <div className="modal-overlay" onClick={() => setShowMembersModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>👥 {selectedProject?.name} - Members</h2>
            
            <table className="data-table" style={{ marginBottom: '15px' }}>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projectMembers.map(member => (
                  <tr key={member.id}>
                    <td>{member.username}</td>
                    <td>{member.role}</td>
                    <td>
                      <button 
                        onClick={() => handleRemoveMember(member.id)}
                        className="btn btn-sm btn-danger"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {projectMembers.length === 0 && (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center' }}>No members</td>
                  </tr>
                )}
              </tbody>
            </table>

            <button 
              onClick={() => setShowAddMemberModal(true)}
              className="btn btn-primary"
              style={{ marginBottom: '15px' }}
            >
              + Add Member
            </button>

            <button onClick={() => setShowMembersModal(false)} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>📥 Import Project</h2>
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Select a project backup JSON file to import.
            </p>
            <div className="form-group">
              <label>Backup File (JSON)</label>
              <input 
                type="file" 
                accept=".json"
                onChange={handleImport}
                disabled={importing}
              />
            </div>
            {importing && <p>Importing...</p>}
            <div className="modal-actions">
              <button onClick={() => setShowImportModal(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Add Member to Project</h2>
            <div className="form-group">
              <label>Select User</label>
              <select 
                onChange={(e) => {
                  if (e.target.value) handleAddMember(e.target.value);
                }}
                defaultValue=""
              >
                <option value="">-- Select User --</option>
                {getAvailableUsers().map(user => (
                  <option key={user.id} value={user.id}>
                    {user.username} ({user.role})
                  </option>
                ))}
              </select>
            </div>
            <button onClick={() => setShowAddMemberModal(false)} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
